const MAX_DIMENSION          = 1920;
const JPEG_QUALITY           = 0.82;
const RESIZE_THRESHOLD_BYTES = 1_000_000; // skip already-small files — nothing to gain

/**
 * Downscale an image client-side before upload. Falls back to the original
 * file whenever decoding isn't possible (e.g. HEIC/HEIF has inconsistent
 * createImageBitmap support outside Safari) or resizing doesn't actually
 * shrink the file.
 */
export async function resizeImageIfNeeded(
  file: File,
  { maxDimension = MAX_DIMENSION, thresholdBytes = RESIZE_THRESHOLD_BYTES }: { maxDimension?: number; thresholdBytes?: number } = {},
): Promise<File> {
  if (file.size <= thresholdBytes) return file;
  if (file.type === 'image/heic' || file.type === 'image/heif') return file;

  try {
    const bitmap = await createImageBitmap(file);
    const scale  = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width  = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width  = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    // PNG sources are re-encoded as JPEG too — lossless PNG barely shrinks a
    // photograph, and these are camera photos of skin, not screenshots/graphics
    // that need crisp edges or alpha transparency.
    const outType = file.type === 'image/webp' ? file.type : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, outType, JPEG_QUALITY),
    );
    if (!blob || blob.size >= file.size) return file;

    const ext  = outType === 'image/webp' ? 'webp' : 'jpg';
    const name = file.name.replace(/\.[^./]+$/, '') + `.${ext}`;
    return new File([blob], name, { type: outType });
  } catch {
    return file;
  }
}
